import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import DataTable from "@/components/DataTable";
import { Button } from "@/components/ui/button";
import PageHeader from "@/components/PageHeader";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Pencil, Trash2, Copy, ClipboardList, X, Sigma } from "lucide-react";
import { toast } from "sonner";

const emptyTpl = { name: "", category: "عام", description: "", components: [] };

export default function Templates() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyTpl);
  // نافذة "حساب المتوسط" — منفصلة عن نافذة القالب نفسها، لاختيار 2-4
  // مكونات موجودة بالفعل + تسمية المتوسط الناتج.
  const [avgOpen, setAvgOpen] = useState(false);
  const [avgName, setAvgName] = useState("");
  const [avgSelected, setAvgSelected] = useState([]);

  const load = () =>
    api.get("/templates").then(({ data }) => setRows(data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(emptyTpl); setOpen(true); };
  const openEdit = async (row) => {
    try {
      const { data } = await api.get(`/templates/${row.id}`);
      setEditing(data); setForm(data); setOpen(true);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const save = async () => {
    if (!form.name.trim()) return toast.error("الاسم مطلوب");
    try {
      const payload = {
        ...form,
        components: form.components.map((c, i) => ({
          name: c.name, max_score: parseFloat(c.max_score) || 0,
          icon: c.icon || "📌", order_index: i,
          is_average: !!c.is_average, source_components: c.source_components || [],
        })),
      };
      if (editing) await api.put(`/templates/${editing.id}`, payload);
      else await api.post("/templates", payload);
      toast.success(editing ? "تم تحديث القالب" : "تم إنشاء القالب");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const dup = async (row) => {
    const name = window.prompt("اسم النسخة الجديدة:", `${row.name} (نسخة)`);
    if (!name) return;
    try { await api.post(`/templates/${row.id}/duplicate`, null, { params: { new_name: name } });
      toast.success("تم تكرار القالب"); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (row) => {
    if (row.used_by > 0 && !window.confirm(`القالب مستخدم في ${row.used_by} مادة. حذفه لن يحذف الدرجات. متابعة؟`)) return;
    if (row.used_by === 0 && !window.confirm(`حذف القالب «${row.name}»؟`)) return;
    try { await api.delete(`/templates/${row.id}`); toast.success("تم الحذف"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const addComp = () =>
    setForm((f) => ({ ...f, components: [...f.components, { name: "", max_score: 0, icon: "📌" }] }));
  const updComp = (i, patch) =>
    setForm((f) => ({ ...f, components: f.components.map((c, j) => j === i ? { ...c, ...patch } : c) }));
  const rmComp = (i) =>
    setForm((f) => ({ ...f, components: f.components.filter((_, j) => j !== i) }));

  // "حساب المتوسط" — اختياري وغير إلزامي؛ إضافته لا تؤثر على أي قالب
  // آخر ولا على أي مرحلة أخرى ما لم يستخدمها المستخدم بنفسه صراحةً.
  const nonAverageComps = form.components.filter((c) => !c.is_average);
  const openAvgPicker = () => {
    if (nonAverageComps.length < 2) return toast.error("محتاج على الأقل مكوّنين موجودين قبل إضافة متوسط");
    setAvgName(""); setAvgSelected([]); setAvgOpen(true);
  };
  const toggleAvgSelect = (name) => {
    setAvgSelected((prev) => prev.includes(name)
      ? prev.filter((n) => n !== name)
      : (prev.length >= 4 ? prev : [...prev, name]));
  };
  const confirmAddAverage = () => {
    if (!avgName.trim()) return toast.error("اكتب اسم المتوسط");
    if (avgSelected.length < 2) return toast.error("اختر مكوّنين على الأقل");
    const sourceMaxes = form.components.filter((c) => avgSelected.includes(c.name)).map((c) => parseFloat(c.max_score) || 0);
    const avgMax = sourceMaxes.reduce((a, b) => a + b, 0) / sourceMaxes.length;
    setForm((f) => ({
      ...f,
      components: [...f.components, {
        name: avgName.trim(), icon: "📊", max_score: Math.round(avgMax * 100) / 100,
        is_average: true, source_components: avgSelected,
      }],
    }));
    setAvgOpen(false);
    toast.success("تمت إضافة المتوسط — لن تُحسَب المكوّنات المصدر مرتين في الإجمالي");
  };

  // إجمالي الدرجات المعروض: يستبعد أي مكوّن يُستخدَم كمصدر لمتوسط (حتى لا
  // يُحتسَب مرتين — مرة كمكوّن مستقل، ومرة ضمن المتوسط الناتج عنه) — بالضبط
  // نفس منطق حساب مجموع أعمال السنة الفعلي في شاشة رصد الدرجات.
  const averagedSourceNames = new Set(form.components.filter((c) => c.is_average).flatMap((c) => c.source_components || []));
  const totalScore = form.components.reduce(
    (a, c) => averagedSourceNames.has(c.name) ? a : a + (parseFloat(c.max_score) || 0), 0
  );

  const columns = [
    { key: "name", header: "اسم القالب",
      render: (r) => <span className="font-medium">{r.name}</span> },
    { key: "category", header: "الفئة",
      render: (r) => (
        <span className="inline-flex px-2 py-0.5 rounded-md text-[11px] font-medium bg-primary/8 text-primary">
          {r.category}
        </span>
      ) },
    { key: "component_count", header: "المكوّنات", align: "end" },
    { key: "total_score", header: "إجمالي الدرجات", align: "end", render: (r) => <span className="tabular-nums">{r.total_score}</span> },
    { key: "used_by", header: "مواد مطبَّقة", align: "end",
      render: (r) => (
        <span className={"tabular-nums " + (r.used_by > 0 ? "text-primary font-medium" : "text-muted-foreground")}>
          {r.used_by}
        </span>
      ) },
  ];

  return (
    <div className="space-y-6" data-testid="templates-page">
      <PageHeader
        icon={ClipboardList}
        overline="الشؤون الأكاديمية"
        title="قوالب أعمال السنة"
        subtitle="أنشئ قوالب مكوّنات مرنة تُطبَّق تلقائيًا على المواد لتوحيد بنية التقييم."
        badge={`${rows.length} قالب`}
        actions={
          <Button
            onClick={openNew}
            data-testid="template-add-btn"
            className="h-10 bg-white text-[#0E7469] hover:bg-[#F4D06F] hover:text-[#083F38] font-semibold"
          >
            <Plus className="h-4 w-4 me-1.5" /> قالب جديد
          </Button>
        }
      />

      <DataTable
        columns={columns} rows={rows}
        searchKeys={["name", "category"]}
        actions={(row) => (
          <div className="flex items-center gap-1 justify-end">
            <Button size="icon" variant="ghost" onClick={() => dup(row)} data-testid={`tpl-dup-${row.id}`}>
              <Copy className="h-4 w-4" />
            </Button>
            <Button size="icon" variant="ghost" onClick={() => openEdit(row)} data-testid={`tpl-edit-${row.id}`}>
              <Pencil className="h-4 w-4" />
            </Button>
            <Button size="icon" variant="ghost" className="text-destructive" onClick={() => del(row)} data-testid={`tpl-del-${row.id}`}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        )}
        emptyText="لا توجد قوالب."
        testId="templates-table"
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" dir="rtl">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              <ClipboardList className="inline h-5 w-5 me-1.5" />
              {editing ? "تعديل قالب" : "قالب جديد"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5 col-span-2">
                <Label>اسم القالب *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="tpl-form-name" />
              </div>
              <div className="space-y-1.5">
                <Label>الفئة</Label>
                <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label>وصف مختصر</Label>
                <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
            </div>

            <div className="border-t border-border pt-3">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="text-sm font-medium">المكوّنات</div>
                  <div className="text-[11px] text-muted-foreground">إجمالي الدرجات: <span className="tabular-nums font-medium text-foreground">{totalScore}</span></div>
                </div>
                <div className="flex items-center gap-1.5">
                  <Button size="sm" variant="outline" onClick={openAvgPicker} data-testid="tpl-add-avg">
                    <Sigma className="h-3.5 w-3.5 me-1" /> حساب متوسط
                  </Button>
                  <Button size="sm" variant="outline" onClick={addComp} data-testid="tpl-add-comp">
                    <Plus className="h-3.5 w-3.5 me-1" /> إضافة مكوّن
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                {form.components.length === 0 && (
                  <div className="text-sm text-muted-foreground text-center py-6 border border-dashed border-border rounded-lg">
                    لا توجد مكوّنات بعد.
                  </div>
                )}
                {form.components.map((c, i) => (
                  <div key={i}>
                    <div className="grid grid-cols-[3.5rem_1fr_6.5rem_2.5rem] gap-2 items-center">
                      <Input value={c.icon} onChange={(e) => updComp(i, { icon: e.target.value })} className="text-center" disabled={c.is_average} />
                      <Input placeholder="اسم المكوّن" value={c.name} onChange={(e) => updComp(i, { name: e.target.value })} disabled={c.is_average} />
                      {c.is_average ? (
                        <div className="h-9 flex items-center justify-end px-2 text-[13px] tabular-nums text-muted-foreground" title="محسوبة تلقائيًا من متوسط المكونات المصدر">
                          {c.max_score}
                        </div>
                      ) : (
                        <Input type="number" step="0.5" placeholder="الدرجة" value={c.max_score}
                                onChange={(e) => updComp(i, { max_score: e.target.value })} className="text-end tabular-nums" />
                      )}
                      <Button size="icon" variant="ghost" onClick={() => rmComp(i)} className="text-destructive">
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    {c.is_average && (
                      <div className="mr-14 mt-1 text-[11px] text-primary flex items-center gap-1">
                        <Sigma className="h-3 w-3" /> متوسط: {(c.source_components || []).join("، ")}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>إلغاء</Button>
            <Button onClick={save} data-testid="tpl-form-save">حفظ</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* نافذة اختيار مكونات "حساب المتوسط" */}
      <Dialog open={avgOpen} onOpenChange={setAvgOpen}>
        <DialogContent className="max-w-md" dir="rtl">
          <DialogHeader>
            <DialogTitle className="font-display text-lg">
              <Sigma className="inline h-5 w-5 me-1.5" /> إضافة حساب متوسط
            </DialogTitle>
            <DialogDescription>
              اختر مكوّنين إلى أربعة، وسمِّ المتوسط الناتج. المكوّنات المختارة لن تُحسَب مرة أخرى ضمن الإجمالي — يُحسَب متوسطها بدلًا منها.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label>اسم المتوسط *</Label>
              <Input value={avgName} onChange={(e) => setAvgName(e.target.value)} placeholder="مثال: الاختبار الشهري" data-testid="avg-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label>اختر المكوّنات ({avgSelected.length}/4)</Label>
              <div className="space-y-1.5 max-h-56 overflow-y-auto border border-border rounded-lg p-2">
                {nonAverageComps.map((c) => (
                  <label key={c.name} className="flex items-center gap-2 text-sm px-1.5 py-1 rounded hover:bg-slate-50 cursor-pointer">
                    <input type="checkbox" checked={avgSelected.includes(c.name)}
                           onChange={() => toggleAvgSelect(c.name)}
                           disabled={!avgSelected.includes(c.name) && avgSelected.length >= 4}
                           className="h-4 w-4 accent-primary" />
                    <span>{c.icon} {c.name || "(بلا اسم)"}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAvgOpen(false)}>إلغاء</Button>
            <Button onClick={confirmAddAverage} data-testid="avg-confirm-btn">إضافة</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
